import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RotateCcw, Home, RefreshCw, ShieldAlert, Info } from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

interface DiagnosticInfo {
  badge: string;
  category: string;
  reason: string;
  recommendation: string;
  referenceCode: string;
}

/**
 * Safely parses any runtime error and returns user-friendly, non-technical explanations.
 * Strictly guarantees that internal code variables, function names, or JavaScript runtime
 * identifiers (such as 'isReAuthLoggingIn is not defined', 'ReferenceError', or auth tokens)
 * are NEVER exposed to the student or user on screen.
 */
function getSafeDiagnosticInfo(error: Error | null): DiagnosticInfo {
  const rawMsg = error?.message || (error ? String(error) : '');
  const lower = rawMsg.toLowerCase();

  // 1. Authentication, Re-Auth, Session, Credentials, or Token issues
  if (
    lower.includes('reauth') ||
    lower.includes('isreauth') ||
    lower.includes('auth') ||
    lower.includes('login') ||
    lower.includes('session') ||
    lower.includes('token') ||
    lower.includes('credential') ||
    lower.includes('unauthorized') ||
    lower.includes('forbidden') ||
    lower.includes('jwt')
  ) {
    return {
      badge: 'Authentication Notice',
      category: 'Session Verification Required',
      reason: 'Your study portal session or security credentials encountered a temporary synchronization check with the clinical registry.',
      recommendation: 'Reloading your session will refresh your credentials and allow you to safely resume your active study progress.',
      referenceCode: 'AUTH-SYNC-REFRESH'
    };
  }

  // 2. Variable reference errors, undefined properties, or component initialization hiccups
  if (
    lower.includes('is not defined') ||
    lower.includes('cannot read propert') ||
    lower.includes('undefined') ||
    lower.includes('null') ||
    lower.includes('referenceerror') ||
    lower.includes('typeerror') ||
    lower.includes('syntaxerror')
  ) {
    return {
      badge: 'System Notice',
      category: 'Workspace Interface State Refresh',
      reason: 'The study application encountered an unexpected interface state while initializing a portal component.',
      recommendation: 'Reloading the page will safely reinitialize all portal components with the latest application updates.',
      referenceCode: 'STATE-INIT-REFRESH'
    };
  }

  // 3. Network, fetch, or API connectivity issues
  if (
    lower.includes('network') ||
    lower.includes('fetch') ||
    lower.includes('offline') ||
    lower.includes('timeout') ||
    lower.includes('failed to fetch') ||
    lower.includes('load failed')
  ) {
    return {
      badge: 'Connection Notice',
      category: 'Server Connection Interruption',
      reason: 'The application was temporarily unable to reach the study server or clinical question registry. A momentary connection drop occurred.',
      recommendation: 'Please verify your network connection and reload your session to reconnect.',
      referenceCode: 'NET-CONN-INTERRUPT'
    };
  }

  // 4. Storage or cache discrepancies
  if (
    lower.includes('storage') ||
    lower.includes('quota') ||
    lower.includes('localstorage') ||
    lower.includes('sessionstorage') ||
    lower.includes('json.parse')
  ) {
    return {
      badge: 'Storage Notice',
      category: 'Study Cache Re-synchronization',
      reason: 'A temporary discrepancy was detected in the local browser study session cache.',
      recommendation: 'Reloading the session will re-synchronize your study records from the cloud registry.',
      referenceCode: 'CACHE-SYNC-REFRESH'
    };
  }

  // 5. Default / General study workspace recovery
  return {
    badge: 'Portal Notice',
    category: 'Study Workspace Recovery',
    reason: 'The study application encountered a temporary display state while navigating the clinical learning portal.',
    recommendation: 'Reload your session to restore your view, or return to the main Practice Dashboard.',
    referenceCode: 'PORTAL-STATE-RECOVERY'
  };
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log full technical trace in developer console for diagnosis
    console.error('Study Hub Uncaught Error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/practice/cna/dashboard';
  };

  private handleClearCacheAndReload = () => {
    try {
      sessionStorage.clear();
      // Clear temporary test/session expired query parameters if present
      const url = new URL(window.location.href);
      url.searchParams.delete('test_session_expired');
      url.searchParams.delete('session_expired');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (e) {
      console.warn('Cache clearing error:', e);
    }
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorBoundaryFallback 
          error={this.state.error}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          onClearCache={this.handleClearCacheAndReload}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryFallback: React.FC<{
  error: Error | null;
  onReset: () => void;
  onGoHome: () => void;
  onClearCache: () => void;
}> = ({ error, onReset, onGoHome, onClearCache }) => {
  const tokens = useThemeTokens();
  const diagnostic = getSafeDiagnosticInfo(error);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4 sm:p-6 text-center" role="alert" aria-live="assertive">
      <div className={`max-w-lg w-full p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl ${tokens.cardBg} space-y-6 text-left`}>
        
        {/* Header Visual & Title */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 shrink-0">
            <ShieldAlert size={24} />
          </div>
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${tokens.badge}`}>
                {diagnostic.badge}
              </span>
              <span className="text-[10px] font-mono font-bold text-slate-400 dark:text-slate-500">
                Ref: {diagnostic.referenceCode}
              </span>
            </div>
            <h2 className={`text-xl font-bold text-slate-900 dark:text-white ${tokens.headerFont}`}>
              Practice Portal Recovery
            </h2>
          </div>
        </div>

        {/* Informative Explanation & Plain-Language Reason */}
        <div className="p-4 bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 rounded-2xl space-y-3">
          <div className="space-y-1">
            <p className="text-xs font-black text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
              <Info size={14} className={tokens.accentText} />
              <span>{diagnostic.category}</span>
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
              {diagnostic.reason}
            </p>
          </div>

          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400">
            <p className="font-semibold leading-relaxed">
              <span className="font-bold text-slate-700 dark:text-slate-300">Action: </span>
              {diagnostic.recommendation}
            </p>
          </div>
        </div>

        {/* Primary Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            onClick={onReset}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 rounded-2xl text-xs font-black uppercase tracking-wider shadow-md transition-all active:scale-[0.98] cursor-pointer ${tokens.primaryBtn}`}
          >
            <RotateCcw size={15} />
            <span>Reload Session</span>
          </button>
          <button
            onClick={onGoHome}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 rounded-2xl text-xs font-black uppercase tracking-wider shadow-sm transition-all active:scale-[0.98] cursor-pointer ${tokens.secondaryBtn}`}
          >
            <Home size={15} />
            <span>Practice Dashboard</span>
          </button>
        </div>

        {/* Subtle Reset Cache Utility */}
        <div className="pt-1 text-center">
          <button
            type="button"
            onClick={onClearCache}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
          >
            <RefreshCw size={12} />
            <span>Reset session cache and reload</span>
          </button>
        </div>

      </div>
    </div>
  );
};
